import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { Component, OnInit } from '@angular/core';
import { AppPageService, CommonActionKey, FormHelper, ProductCode, SimpleDialogService } from 'common';
import { AccountData, LoanOtherInfoUpdateDto } from '../account.model';
import { Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AppBarTitleService, AppBarActionsService, MessageService, AppBarAction } from 'common';
import { AccountService } from '../account.service';

@UntilDestroy()
@Component({
    selector: 'ifb-account-profile-other-info-details',
    templateUrl: './account-profile-other-info-details.component.html',
    styleUrls: ['./account-profile-other-info-details.component.scss'],
    standalone: false
})

export class AccountOtherInfoDetailsComponent implements OnInit {
  private saveSub: Subscription;

  form: UntypedFormGroup;
  data: AccountOtherInfoDetailsComponentData;

  readonly ProductCode = ProductCode;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly accountService: AccountService,
    private readonly appPageService: AppPageService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly messageService: MessageService,
    private readonly simpleDialogService: SimpleDialogService,
  ) {

    this.formInit();

    this.appBarActionsService.actions = [
      { id: 'cancel', label: 'Cancel', buttonType: 'button' },
      { id: 'save', label: 'Save', disabled: !this.form.dirty, buttonType: 'submit', buttonAppearance: 'flat', buttonColor: 'primary' }
    ];
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this))
      .subscribe(this.dataInit.bind(this));

    this.appBarActionsService.invoking.pipe(untilDestroyed(this))
      .subscribe(this.actionDispatch.bind(this));

    this.form.statusChanges.pipe(untilDestroyed(this))
      .subscribe(this.actionUpdate.bind(this));

    this.watchChanges();
  }

  private watchChanges(): void {
    this.form.valueChanges.subscribe(() => {
      if (this.form.dirty) {
        this.appBarActionsService.enable('save', true);
      }
    });
  }

  actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void = this[action.id].bind(this);
    actionHandler(action);
  }

  private actionUpdate(): void {
    this.appBarActionsService.label('save', 'Save');
  }

  cancel(): void {
    this.appPageService.back();
  }

  dataInit(data: AccountOtherInfoDetailsComponentData): void {
    this.data = data;
    this.appBarTitleService.title = `${this.data.account.loanNumber} - Other info`;

    this.form.reset({
      description: this.data.account.description,
      externalId: this.data.account.externalId,
      policyNumber: this.data.account.policyNumber,
      policyLimit: this.data.account.policyLimit,
      loanNumber: this.data.account.loanNumber,
      uniqueId: this.data.account.uniqueId,
      applicationNumber: this.data.account.applicationNumber,
      applicationUniqueId: this.data.account.applicationUniqueId
    });

    this.form.markAsPristine();
    this.actionUpdate();
  }

  private formInit(): void {
    this.form = this.formBuilder.group({
      description: [undefined, Validators.compose([Validators.maxLength(256)])],
      externalId: [undefined, Validators.compose([Validators.maxLength(64)])],
      policyNumber: [undefined, Validators.compose([Validators.maxLength(64)])],
      policyLimit: undefined,
      loanNumber: [{ value: undefined, disabled: true }],
      uniqueId: [{ value: undefined, disabled: true }],
      applicationNumber: [{ value: undefined, disabled: true }],
      applicationUniqueId: [{ value: undefined, disabled: true }]
    });
  }

  save(): void {
    FormHelper.showInvalidFormFields(this.form);
    if (this.saveSub || !this.form.valid)
      return;

    this.simpleDialogService.openSimpleConfirmDialog(`Save changes to ${this.data.account.loanNumber}?`, "Confirm")
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm) {
          const update: LoanOtherInfoUpdateDto = {
            externalId: this.form.controls.externalId.value,
            description: this.form.controls.description.value,
            policyNumber: this.form.controls.policyNumber.value,
            policyLimit: this.form.controls.policyLimit.value
          };
          this.accountService.updateLoanOtherInfo(this.data.account.id, update)
            .subscribe(this.saveSuccessHandler.bind(this), this.saveErrorHandler.bind(this));
        }
      });
  }

  private saveSubClear(): void {
    if (this.saveSub)
      this.saveSub.unsubscribe();
    this.saveSub = null;
  }

  private saveErrorHandler(error: any): void {
    this.saveSubClear();
    this.messageService.error(error);
  }

  private saveSuccessHandler(): void {
    this.saveSubClear();
    this.appPageService.back();
  }
}

export interface AccountOtherInfoDetailsComponentData {
  account: AccountData;
}
